/**
 * Mock Active Position Repository
 * Returns sample data for development
 */

import type {
  IActivePositionRepository,
  ActivePosition,
  CreatePositionData,
  UpdatePositionData,
  PositionSnapshot,
} from '../interfaces/IActivePositionRepository'

const mockPositions: ActivePosition[] = [
  {
    id: '1',
    ticker: 'GEV',
    buyInPrice: 420.0,
    openedAt: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
    createdAt: new Date(Date.now() - 30 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 86400000).toISOString(),
  },
  {
    id: '2',
    ticker: 'VRT',
    buyInPrice: 180.0,
    openedAt: new Date(Date.now() - 20 * 86400000).toISOString().slice(0, 10),
    createdAt: new Date(Date.now() - 20 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 20 * 86400000).toISOString(),
  },
  {
    id: '3',
    ticker: 'NVDA',
    buyInPrice: 850.0,
    openedAt: new Date(Date.now() - 15 * 86400000).toISOString().slice(0, 10),
    notes: 'AI infrastructure play',
    createdAt: new Date(Date.now() - 15 * 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 15 * 86400000).toISOString(),
  },
]

function generateMockSnapshots(): PositionSnapshot[] {
  const snapshots: PositionSnapshot[] = []
  const now = new Date()

  for (const pos of mockPositions) {
    const openedDaysAgo = pos.openedAt
      ? Math.floor(
          (now.getTime() - new Date(pos.openedAt).getTime()) / 86400000
        )
      : 30

    for (let i = openedDaysAgo; i >= 0; i--) {
      const date = new Date(now)
      date.setDate(date.getDate() - i)

      // Skip weekends
      const day = date.getDay()
      if (day === 0 || day === 6) continue

      const basePnl = (openedDaysAgo - i) * 3 + Math.sin(i * 0.5) * 50
      const noise = (Math.random() - 0.4) * 30
      const pnl = Math.round((basePnl + noise) * 100) / 100
      const pnlPercent =
        Math.round((pnl / pos.buyInPrice) * 100 * 100) / 100
      const closePrice =
        Math.round((pos.buyInPrice + pnl) * 100) / 100

      snapshots.push({
        id: `snap-${pos.id}-${i}`,
        positionId: pos.id,
        ticker: pos.ticker,
        snapshotDate: date.toISOString().slice(0, 10),
        closePrice,
        buyInPrice: pos.buyInPrice,
        pnl,
        pnlPercent,
      })
    }
  }

  return snapshots
}

export class MockActivePositionRepository
  implements IActivePositionRepository
{
  private positions = [...mockPositions]
  private snapshots = generateMockSnapshots()

  async getPositions(): Promise<ActivePosition[]> {
    await new Promise((r) => setTimeout(r, 300))
    return this.positions
  }

  async createPosition(data: CreatePositionData): Promise<ActivePosition> {
    await new Promise((r) => setTimeout(r, 300))
    const position: ActivePosition = {
      id: crypto.randomUUID(),
      ticker: data.ticker.toUpperCase().trim(),
      buyInPrice: data.buyInPrice,
      openedAt: data.openedAt,
      notes: data.notes,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    this.positions.push(position)
    return position
  }

  async updatePosition(
    id: string,
    data: UpdatePositionData
  ): Promise<ActivePosition> {
    await new Promise((r) => setTimeout(r, 300))
    const index = this.positions.findIndex((p) => p.id === id)
    if (index === -1) throw new Error('Position not found')

    const existing = this.positions[index]!
    const updated: ActivePosition = {
      id: existing.id,
      ticker: data.ticker !== undefined ? data.ticker.toUpperCase().trim() : existing.ticker,
      buyInPrice: data.buyInPrice !== undefined ? data.buyInPrice : existing.buyInPrice,
      openedAt: data.openedAt !== undefined ? data.openedAt || undefined : existing.openedAt,
      notes: data.notes !== undefined ? data.notes || undefined : existing.notes,
      createdAt: existing.createdAt,
      updatedAt: new Date().toISOString(),
    }

    this.positions[index] = updated
    return updated
  }

  async deletePosition(id: string): Promise<void> {
    await new Promise((r) => setTimeout(r, 200))
    this.positions = this.positions.filter((p) => p.id !== id)
    this.snapshots = this.snapshots.filter((s) => s.positionId !== id)
  }

  async getSnapshots(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _days?: number
  ): Promise<PositionSnapshot[]> {
    await new Promise((r) => setTimeout(r, 200))
    return this.snapshots
  }
}
